import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import Select from "react-select";
import StandardDatePicker from "./StandardDatePicker";
import { selectStyles } from "../helper/SelectStyles";
import { getClientById, saveClient, updateClient } from "../Apis/ClientAPIs";
import { getLocations } from "../Apis/MasterAPIs";
import { getProducts } from "../Apis/ProductAPIs";
import { getCategories } from "../Apis/CategoryAPIs";
import { getManagers } from "../Apis/ManagerAPIs";
import { getClientGroups } from "../Apis/ClientGroupAPIs";

const ClientFormLayer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isEdit = !!id;

  const [formData, setFormData] = useState({
    companyName: "",
    keyPerson: "",
    email: "",
    mobile1: "",
    mobile2: "",
    mobile3: "",
    landline: "",
    address: "",
    state: "",
    pincode: "",
    products: [],
    customerSince: "",
    ratings: "",
    segment: "",
    isActive: true,
    interestedLocations: [],
    whatsappNumber: "",
    websiteLink: "",
    managerId: "",
    groupId: "",
    categoryId: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [productOptions, setProductOptions] = useState([]);
  const [filteredProductOptions, setFilteredProductOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Manager & Group state
  const [managerOptions, setManagerOptions] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      const [locRes, prodRes, catRes, managerRes, groupRes] = await Promise.all([
        getLocations(),
        getProducts(),
        getCategories(),
        getManagers({ limit: 1000 }),
        getClientGroups({ limit: 1000 }),
      ]);

      if (locRes.status) {
        const locData = locRes.response?.data || locRes.response;
        setLocationOptions(Array.isArray(locData) ? locData.map(l => ({ value: l._id || l.id, label: l.name })) : []);
      }
      let pOptions = [];
      let cOptions = [];
      if (prodRes.status) {
        const prodData = prodRes.response?.data || prodRes.response;
        pOptions = Array.isArray(prodData) ? prodData.map(p => ({
          value: p._id || p.id,
          label: p.productName,
          categoryId: p.categoryId || (typeof p.category === 'object' ? p.category._id : p.category)
        })) : [];
        setProductOptions(pOptions);
        setFilteredProductOptions(pOptions);
      }
      if (catRes.status) {
        const catData = catRes.response?.data || catRes.response;
        cOptions = Array.isArray(catData) ? catData.map(c => ({ value: c._id || c.id, label: c.name })) : [];
        setCategoryOptions(cOptions);
      }
      if (managerRes.status) {
        const mData = managerRes.response?.data || managerRes.response || [];
        setManagerOptions(Array.isArray(mData) ? mData.map(m => ({ value: m._id || m.id, label: m.name })) : []);
      }
      if (groupRes && groupRes.status) {
        const gData = groupRes.response?.data || [];
        setGroupOptions(gData.map(g => ({ value: g._id || g.id, label: g.groupName, manager: g.manager, groupType: g.groupType })));
      }

      if (isEdit) {
        const clientRes = await getClientById(id);
        if (clientRes.status && clientRes.response) {
          const client = clientRes.response?.data || clientRes.response;
          const clientData = {
            ...client,
            mobile3: client.mobile3 || "",
            address: client.address || client.street || "",
            customerSince: (() => {
              if (!client.customerSince) return "";
              if (String(client.customerSince).trim().length === 4) return String(client.customerSince).trim();
              const d = new Date(client.customerSince);
              return !isNaN(d.getFullYear()) ? String(d.getFullYear()) : "";
            })(),
            ratings: (() => {
              const r = String(client.ratings || "OTHER EXHIBITOR");
              const mapping = { "1": "VIP", "2": "ENQUIRY", "3": "OTHER EXHIBITOR", "4": "OWN EXHIBITOR" };
              return mapping[r] || r;
            })(),
            segment: client.segment || "",
            isActive: client.isActive ?? true,
            managerId: client.managerId?._id || client.managerId || "",
            groupId: client.groupId?._id || client.groupId || "",
            categoryId: client.categoryId?._id || client.categoryId || "",
            groupType: client.groupType || "",
            products: Array.isArray(client.products)
              ? client.products.map(p => typeof p === 'object' ? (p._id || p.id) : p)
              : [],
            interestedLocations: Array.isArray(client.interestedLocations)
              ? client.interestedLocations.map(l => typeof l === 'object' ? (l._id || l.id) : l)
              : []
          };
          setFormData(clientData);

          // Add client's manager and group to options if not present
          if (client.managerId && typeof client.managerId === 'object') {
            const mId = client.managerId._id || client.managerId.id;
            const mName = client.managerId.name;
            if (mId && mName) {
              setManagerOptions(prev => {
                if (!prev.some(o => o.value === mId)) {
                  return [...prev, { value: mId, label: mName }];
                }
                return prev;
              });
            }
          }
          if (client.groupId && typeof client.groupId === 'object') {
            const gId = client.groupId._id || client.groupId.id;
            const gName = client.groupId.groupName || client.groupId.name;
            if (gId && gName) {
              setGroupOptions(prev => {
                if (!prev.some(o => o.value === gId)) {
                  return [...prev, { value: gId, label: gName, manager: client.managerId }];
                }
                return prev;
              });
            }
          }

          // Auto-fill/select Exhibition Type category when editing
          const clientCatId = client.categoryId?._id || client.categoryId;
          if (clientCatId) {
            const matchingCat = cOptions.find(c => c.value === clientCatId);
            if (matchingCat) {
              setSelectedCategory(matchingCat);
              setFilteredProductOptions(pOptions.filter(p => p.categoryId === clientCatId));
            }
          } else if (clientData.products && clientData.products.length > 0) {
            const firstProdId = clientData.products[0];
            const matchingProd = pOptions.find(p => p.value === firstProdId);
            if (matchingProd && matchingProd.categoryId) {
              const matchingCat = cOptions.find(c => c.value === matchingProd.categoryId);
              if (matchingCat) {
                setSelectedCategory(matchingCat);
                setFilteredProductOptions(pOptions.filter(p => p.categoryId === matchingProd.categoryId));
              }
            }
          }
        }
      }
      setLoading(false);
    };
    fetchInitialData();
  }, [isEdit, id]);

  useEffect(() => {
    if (!isEdit && location.state) {
      const { managerId, groupId, managerName, groupName } = location.state;
      if (groupId || managerId) {
        setFormData(prev => ({
          ...prev,
          managerId: managerId || "",
          groupId: groupId || "",
        }));

        if (groupId && groupName) {
          setGroupOptions(prev => {
            if (!prev.some(o => o.value === groupId)) {
              return [...prev, { value: groupId, label: groupName, manager: managerId }];
            }
            return prev;
          });
        }

        if (managerId && managerName) {
          setManagerOptions(prev => {
            if (!prev.some(o => o.value === managerId)) {
              return [...prev, { value: managerId, label: managerName }];
            }
            return prev;
          });
        }
      }
    }
  }, [location.state, isEdit]);

  useEffect(() => {
    if (groupOptions.length > 0 && formData.groupId) {
      const selectedGroupObj = groupOptions.find(o => o.value === formData.groupId);
      if (selectedGroupObj && selectedGroupObj.groupType) {
        setFormData(prev => ({ ...prev, groupType: selectedGroupObj.groupType }));
      }
    }
  }, [groupOptions, formData.groupId]);

  const ratingOptions = [
    { value: "VIP", label: "VIP" },
    { value: "ENQUIRY", label: "ENQUIRY" },
    { value: "OTHER EXHIBITOR", label: "OTHER EXHIBITOR" },
    { value: "OWN EXHIBITOR", label: "OWN EXHIBITOR" }
  ];

  const segmentOptions = [
    { value: "B2B", label: "B2B" },
    { value: "B2C", label: "B2C" },
    { value: "OTHERS", label: "OTHERS" }
  ];

  const statusOptions = [
    { value: true, label: "Active" },
    { value: false, label: "Inactive" },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Alphabet-only fields
    if (["city", "state"].includes(name)) {
      const alphabetOnly = value.replace(/[^A-Za-z\s]/g, "");
      setFormData((prev) => ({ ...prev, [name]: alphabetOnly }));
      return;
    }

    // Numeric-only fields
    const numericFields = ["mobile1", "mobile2", "mobile3", "whatsappNumber", "landline", "pincode", "customerSince"];
    if (numericFields.includes(name)) {
      const isDigitsOnly = /^\d*$/.test(value);
      if (!isDigitsOnly) return;

      // Length constraints
      if (name === "pincode" && value.length > 6) return;
      if (name === "customerSince" && value.length > 4) return;
      if (["mobile1", "mobile2", "mobile3", "whatsappNumber"].includes(name) && value.length > 10) return;
      if (name === "landline" && value.length > 12) return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, selectedOptions) => {
    if (name === 'selectedCategory') {
      setSelectedCategory(selectedOptions);
      if (selectedOptions) {
        const categoryProducts = productOptions.filter(p => p.categoryId === selectedOptions.value);
        setFilteredProductOptions(categoryProducts);

        if (categoryProducts.length > 0) {
          // Auto-fill all products under this category
          const productIds = categoryProducts.map(p => p.value);
          setFormData(prev => ({ ...prev, categoryId: selectedOptions.value, products: productIds }));
          // Clear any validation errors for category and products
          setErrors(prev => {
            const newErrs = { ...prev };
            delete newErrs.products;
            delete newErrs.selectedCategory;
            return newErrs;
          });
        } else {
          // Clear products if none exist under this category and show warning
          setFormData(prev => ({ ...prev, categoryId: selectedOptions.value, products: [] }));
          toast.warning("No products found for this exhibition type");
        }
      } else {
        setFilteredProductOptions(productOptions);
        setFormData(prev => ({ ...prev, categoryId: "", products: [] }));
      }
      return;
    }

    if (name === 'groupId') {
      const val = selectedOptions ? selectedOptions.value : "";
      const selectedGroupObj = groupOptions.find(o => o.value === val);
      let managerId = "";
      if (selectedGroupObj && selectedGroupObj.manager) {
        if (typeof selectedGroupObj.manager === 'object') {
          managerId = selectedGroupObj.manager._id || selectedGroupObj.manager.id || "";
        } else {
          managerId = selectedGroupObj.manager;
        }
      }
      setFormData(prev => ({ ...prev, groupId: val, managerId: managerId, groupType: selectedGroupObj?.groupType || "" }));
      setErrors(prev => {
        const newErrs = { ...prev };
        if (val) delete newErrs.groupId;
        if (managerId || selectedGroupObj?.groupType === "Expo") delete newErrs.managerId;
        return newErrs;
      });
      return;
    }

    if (Array.isArray(selectedOptions)) {
      const values = selectedOptions.map(opt => opt.value);
      setFormData(prev => ({ ...prev, [name]: values }));
    } else {
      setFormData(prev => ({ ...prev, [name]: selectedOptions ? selectedOptions.value : "" }));
    }
  };

  const validate = () => {
    const newErrors = {};
    const alphabetRegex = /^[A-Za-z\s]+$/;

    if (formData.groupType !== "Expo" && !formData.managerId) {
      newErrors.managerId = "Manager is Required";
    }
    if (!formData.groupId) newErrors.groupId = "Group is Required";

    if (!formData.companyName.trim()) {
      newErrors.companyName = "Company Name is Required";
    }

    if (!formData.keyPerson.trim()) {
      newErrors.keyPerson = "Key Person is Required";
    }

    if (formData.email && formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    if (!formData.mobile1) {
      newErrors.mobile1 = "Mobile Number is Required";
    } else if (formData.mobile1.length !== 10) {
      newErrors.mobile1 = "Must be exactly 10 digits";
    }

    if (formData.whatsappNumber && formData.whatsappNumber.length !== 10) {
      newErrors.whatsappNumber = "Must be exactly 10 digits";
    }

    if (formData.mobile2 && formData.mobile2.length !== 10) {
      newErrors.mobile2 = "Must be exactly 10 digits";
    }

    if (formData.mobile3 && formData.mobile3.length !== 10) {
      newErrors.mobile3 = "Must be exactly 10 digits";
    }

    if (formData.customerSince) {
      const year = parseInt(formData.customerSince);
      const currentYear = new Date().getFullYear();
      if (isNaN(year) || year < 1900 || formData.customerSince.length !== 4) {
        newErrors.customerSince = "Must be a valid 4-digit year";
      } else if (year > currentYear) {
        newErrors.customerSince = "Year cannot be in the future";
      }
    }

    if (!selectedCategory) {
      newErrors.selectedCategory = "Exhibition Type is Required";
    }
    if (!formData.interestedLocations || formData.interestedLocations.length === 0) newErrors.interestedLocations = "At least one Location is Required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setSubmitting(true);

    // Map address to street field to keep backend database schema compatible
    const payload = {
      ...formData,
      street: formData.address,
      area: "",
      city: "",
    };

    let res;
    if (isEdit) {
      res = await updateClient(id, payload);
    } else {
      res = await saveClient(payload);
    }

    if (res.status) {
      toast.success(`Client ${isEdit ? "Updated" : "Added"} Successfully`);
      const fromPath = location.state?.from;
      if (fromPath) {
        navigate(fromPath);
      } else {
        const gId = formData.groupId?._id || formData.groupId || "";
        if (gId) {
          navigate(`/client-groups/view/${gId}`);
        } else {
          navigate("/clients");
        }
      }
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  const isManagerDisabled = (!!location.state?.fromGroupView && !!formData.managerId) || isEdit;
  const isGroupDisabled = !!location.state?.fromGroupView || isEdit;

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-header border-bottom px-24 py-12 d-flex align-items-center justify-content-between" style={{ background: "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)" }}>
        <h6 className="text-white pb-2 mb-0" style={{ fontWeight: "700" }}>{isEdit ? "EDIT CLIENT" : "ADD CLIENT"}</h6>
        {location.state?.from ? (
          <button
            type="button"
            onClick={() => navigate(location.state.from)}
            className="btn btn-sm d-flex align-items-center gap-1"
            style={{ backgroundColor: "#f7e7da", color: "black", fontWeight: "600", border: "0" }}
          >
            <Icon icon="solar:arrow-left-bold" /> Back
          </button>
        ) : formData.groupId ? (
          <button
            type="button"
            onClick={() => {
              const gId = formData.groupId?._id || formData.groupId || "";
              navigate(`/client-groups/view/${gId}`);
            }}
            className="btn btn-sm d-flex align-items-center gap-1"
            style={{ backgroundColor: "#f7e7da", color: "black", fontWeight: "600", border: "0" }}
          >
            <Icon icon="solar:arrow-left-bold" /> Back to Group
          </button>
        ) : (
          <Link to="/clients" className="btn btn-sm d-flex align-items-center gap-1" style={{ backgroundColor: "#f7e7da", color: "black", fontWeight: "600" }}>
            <Icon icon="solar:arrow-left-bold" /> List Clients
          </Link>
        )}
      </div>
      <div className="card-body p-24">
        <form onSubmit={handleSubmit} noValidate>
          <div className="row gy-4">

            {/* ── Manager & Group ── */}
            <div className="col-md-6">
              <label className="form-label fw-bold">Manager {formData.groupType !== "Expo" && <span className="text-danger">*</span>}</label>
              <Select
                options={managerOptions}
                isSearchable
                isDisabled={true}
                value={managerOptions.find(o => o.value === formData.managerId) || null}
                onChange={(val) => handleSelectChange('managerId', val)}
                styles={selectStyles(!!errors.managerId)}
                placeholder={formData.groupType === "Expo" ? "Manager not required for Expo group" : "Manager will be auto-filled..."}
                menuPortalTarget={document.body}
                maxMenuHeight={450}
              />
              {errors.managerId && <div className="text-danger text-sm mt-1">{errors.managerId}</div>}
            </div>
            <div className="col-md-6">
              <label className="form-label fw-bold">Group <span className="text-danger">*</span></label>
              <Select
                options={groupOptions}
                isSearchable
                isDisabled={isGroupDisabled}
                isLoading={loadingGroups}
                value={groupOptions.find(o => o.value === formData.groupId) || null}
                onChange={(val) => handleSelectChange('groupId', val)}
                styles={selectStyles(!!errors.groupId)}
                placeholder="Select Group..."
                menuPortalTarget={document.body}
                maxMenuHeight={350}
              />
              {errors.groupId && <div className="text-danger text-sm mt-1">{errors.groupId}</div>}
            </div>

            {/* ── Divider ── */}
            <div className="col-12">
              <hr className="my-0" style={{ borderColor: "#f0e0d6" }} />
            </div>

            {/* Company, Key Person, Email */}
            <div className="col-md-4">
              <label className="form-label fw-bold">Company Name <span className="text-danger">*</span></label>
              <input
                type="text"
                name="companyName"
                className={`form-control radius-8 ${errors.companyName ? 'is-invalid' : ''}`}
                value={formData.companyName}
                onChange={handleInputChange}
                placeholder="Enter Company Name"
              />
              {errors.companyName && <div className="invalid-feedback">{errors.companyName}</div>}
            </div>
            <div className="col-md-4">
              <label className="form-label fw-bold">Key Person <span className="text-danger">*</span></label>
              <input
                type="text"
                name="keyPerson"
                className={`form-control radius-8 ${errors.keyPerson ? 'is-invalid' : ''}`}
                value={formData.keyPerson}
                onChange={handleInputChange}
                placeholder="Enter Key Person"
              />
              {errors.keyPerson && <div className="invalid-feedback">{errors.keyPerson}</div>}
            </div>
            <div className="col-md-4">
              <label className="form-label fw-bold">Email</label>
              <input
                type="email"
                name="email"
                className={`form-control radius-8 ${errors.email ? 'is-invalid' : ''}`}
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter Email"
                autoComplete="off"
              />
              {errors.email && <div className="invalid-feedback">{errors.email}</div>}
            </div>

            {/* Mobile 1, Mobile 2, Mobile 3 */}
            <div className="col-md-4">
              <label className="form-label fw-bold">Mobile 1 <span className="text-danger">*</span></label>
              <input
                type="text"
                name="mobile1"
                className={`form-control radius-8 ${errors.mobile1 ? 'is-invalid' : ''}`}
                value={formData.mobile1}
                onChange={handleInputChange}
                placeholder="Enter Mobile 1"
              />
              {errors.mobile1 && <div className="invalid-feedback">{errors.mobile1}</div>}
            </div>
            <div className="col-md-4">
              <label className="form-label fw-bold">Mobile 2</label>
              <input
                type="text"
                name="mobile2"
                className={`form-control radius-8 ${errors.mobile2 ? 'is-invalid' : ''}`}
                value={formData.mobile2}
                onChange={handleInputChange}
                placeholder="Enter Mobile 2"
                autoComplete="off"
              />
              {errors.mobile2 && <div className="invalid-feedback">{errors.mobile2}</div>}
            </div>
            <div className="col-md-4">
              <label className="form-label fw-bold">Mobile 3</label>
              <input
                type="text"
                name="mobile3"
                className={`form-control radius-8 ${errors.mobile3 ? 'is-invalid' : ''}`}
                value={formData.mobile3}
                onChange={handleInputChange}
                placeholder="Enter Mobile 3"
                autoComplete="off"
              />
              {errors.mobile3 && <div className="invalid-feedback">{errors.mobile3}</div>}
            </div>

            {/* Whatsapp Number, Website Link, Landline */}
            <div className="col-md-4">
              <label className="form-label fw-bold">Whatsapp Number</label>
              <input
                type="text"
                name="whatsappNumber"
                className={`form-control radius-8 ${errors.whatsappNumber ? 'is-invalid' : ''}`}
                value={formData.whatsappNumber}
                onChange={handleInputChange}
                placeholder="Enter Whatsapp Number"
                autoComplete="off"
              />
              {errors.whatsappNumber && <div className="invalid-feedback">{errors.whatsappNumber}</div>}
            </div>
            <div className="col-md-4">
              <label className="form-label fw-bold">Website Link</label>
              <input
                type="text"
                name="websiteLink"
                className={`form-control radius-8 ${errors.websiteLink ? 'is-invalid' : ''}`}
                value={formData.websiteLink}
                onChange={handleInputChange}
                placeholder="https://example.com"
                autoComplete="off"
              />
              {errors.websiteLink && <div className="invalid-feedback">{errors.websiteLink}</div>}
            </div>
            <div className="col-md-4">
              <label className="form-label fw-bold">Landline</label>
              <input
                type="text"
                name="landline"
                className={`form-control radius-8 ${errors.landline ? 'is-invalid' : ''}`}
                value={formData.landline}
                onChange={handleInputChange}
                placeholder="Enter Landline"
                autoComplete="off"
              />
              {errors.landline && <div className="invalid-feedback">{errors.landline}</div>}
            </div>

            {/* Address (Combined Street, Area, City) */}
            {/* <div className="col-12">
              <label className="form-label fw-bold">Address</label>
              <input
                type="text"
                name="address"
                className="form-control radius-8"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Enter Full Address"
                autoComplete="off"
              />
            </div> */}

            {/* State, Pincode, Customer Since */}
            <div className="col-md-4">
              <label className="form-label fw-bold">City</label>
              <input type="text" name="state" className="form-control radius-8" value={formData.city} onChange={handleInputChange} placeholder="Enter City" />
            </div>
            <div className="col-md-4">
              <label className="form-label fw-bold">Pincode</label>
              <input type="text" name="pincode" className="form-control radius-8" value={formData.pincode} onChange={handleInputChange} placeholder="Enter Pincode" />
            </div>
            <div className="col-md-4">
              <label className="form-label fw-bold">Customer Since</label>
              <input
                type="text"
                name="customerSince"
                className={`form-control radius-8 ${errors.customerSince ? 'is-invalid' : ''}`}
                value={formData.customerSince}
                onChange={handleInputChange}
                placeholder="Enter Year (e.g. 2024)"
                autoComplete="off"
              />
              {errors.customerSince && <div className="text-danger text-sm mt-1">{errors.customerSince}</div>}
            </div>

            {/* Ratings, Segment & Status */}
            <div className="col-md-4">
              <label className="form-label fw-bold">Ratings</label>
              <Select
                options={ratingOptions}
                value={ratingOptions.find(opt => opt.value === formData.ratings)}
                onChange={(val) => handleSelectChange('ratings', val)}
                styles={selectStyles()}
                placeholder="Select Rating"
                menuPortalTarget={document.body}
                maxMenuHeight={350}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label fw-bold">Segment</label>
              <Select
                options={segmentOptions}
                value={segmentOptions.find(opt => opt.value === formData.segment)}
                onChange={(val) => handleSelectChange('segment', val)}
                styles={selectStyles()}
                placeholder="Select Segment"
                menuPortalTarget={document.body}
                maxMenuHeight={350}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label fw-bold">Status</label>
              <Select
                options={statusOptions}
                value={statusOptions.find(opt => opt.value === formData.isActive)}
                onChange={(val) => handleSelectChange('isActive', val)}
                styles={selectStyles()}
                placeholder="Select Status"
                menuPortalTarget={document.body}
                maxMenuHeight={350}
              />
            </div>

            {/* Products & Locations */}
            <div className="col-md-6">
              <label className="form-label fw-bold">Exhibition Type <span className="text-danger">*</span></label>
              <Select
                options={categoryOptions}
                value={selectedCategory}
                onChange={(selected) => handleSelectChange('selectedCategory', selected)}
                styles={selectStyles(!!errors.selectedCategory)}
                placeholder="Select Exhibition Type to filter products"
                isClearable
                menuPortalTarget={document.body}
                maxMenuHeight={350}
              />
              {errors.selectedCategory && <div className="text-danger text-sm mt-1">{errors.selectedCategory}</div>}
            </div>
            <div className="col-md-6">
              <label className="form-label fw-bold">Products</label>
              <Select
                isMulti
                options={filteredProductOptions}
                value={productOptions.filter(opt => formData.products?.includes(opt.value))}
                onChange={(selected) => handleSelectChange('products', selected)}
                styles={selectStyles()}
                placeholder="Select Products"
                menuPortalTarget={document.body}
                maxMenuHeight={350}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-bold">Client Interested Locations <span className="text-danger">*</span></label>
              <Select
                isMulti
                options={locationOptions}
                value={locationOptions.filter(opt => formData.interestedLocations?.includes(opt.value))}
                onChange={(selected) => handleSelectChange('interestedLocations', selected)}
                styles={selectStyles(!!errors.interestedLocations)}
                placeholder="Select Locations"
                menuPortalTarget={document.body}
                maxMenuHeight={350}
              />
              {errors.interestedLocations && <div className="text-danger text-sm mt-1">{errors.interestedLocations}</div>}
            </div>

            {/* Action Buttons */}
            <div className="col-12 mt-32 d-flex justify-content-end gap-3">
              <button
                type="button"
                className="btn radius-8 px-40 py-11"
                style={{ backgroundColor: "white", color: "#d9534f", fontWeight: "700", border: '1px solid #d9534f' }}
                onClick={() => {
                  const fromPath = location.state?.from;
                  if (fromPath) {
                    navigate(fromPath);
                  } else {
                    const gId = formData.groupId?._id || formData.groupId || "";
                    if (gId) {
                      navigate(`/client-groups/view/${gId}`);
                    } else {
                      navigate("/clients");
                    }
                  }
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn radius-8 px-48 py-11"
                disabled={submitting}
                style={{ backgroundColor: "#ed1c24", color: "white", fontWeight: "700" }}
              >
                {submitting ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClientFormLayer;
